import React, { useState, useEffect, useRef } from 'react'
import { cx } from '@monorepo/helper/cx'

import FilterArea from '@/components/filterArea'
import Card from '@/components/card'
import useClickOutside from '@/lib/useClickOutside'
import Img from '@/components/Img'

import withNewLines from '@/lib/withNewLines'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import type { Project, ProjectsQuery } from '@/.generated/sanityTypes/graphql'
import { getTypedLocale, isLocalString } from '../lib/typeHelper'
import { sorter } from '@monorepo/helper/utils/sorting'

type Domain = {
  id?: string
  name?: string
  value?: string
}

const ProjectCard = ({ project }: any) => {
  const locale = getTypedLocale(useRouter().locale)
  const t = useTranslations('projects')
  const dateRange =
    project.startYear &&
    (project.startYear === project.endYear
      ? project.startYear
      : !project.endYear
        ? `${t('since')} ${project.startYear}`
        : `${project.startYear}–${project.endYear ?? ''}`)

  return (
    <Link
      href={`/projects/${project.slug.current}`}
      className="w-full min-w-48 flex-initial pb-5 sm:w-full sm:p-5 lg:w-1/2"
    >
      <div className="tbf-hover flex h-full flex-col bg-white">
        {project.projectImages && project.projectImages.length > 0 && (
          /* the min-height fixes an IE11 flex column stretching bug */
          <Img
            sizes="(min-width: 1360px) 620px, (min-width: 780px) 44.64vw, 100vw"
            image={project.projectImages[0]}
            objectFit="cover"
            className="sm:max-h-144 h-full max-h-40 min-h-px w-full lg:max-h-72"
          />
        )}
        <div className="my-auto flex items-center justify-between bg-white">
          <div className="m-4 flex flex-col sm:pr-6">
            <h1 className="break-words text-xl leading-none text-red-500 sm:text-2xl">
              {withNewLines(project.title[locale])}
            </h1>
            <h2 className="text-normal mt-1 leading-none sm:text-xl">
              {project.location && project.location[locale]}
            </h2>
          </div>
          <div className="whitespace-no-wrap text-normal m-4 self-center sm:text-xl">
            <div className="float-right">{dateRange}</div>
          </div>
        </div>
      </div>
    </Link>
  )
}

type ProjectFiltersProps = {
  domains: Domain[]
  applyDomainFiltering: (selectionMap: Map<string, boolean>) => void
  selectedDomains: Map<string, boolean>
  services: any
  applyServiceFiltering: (selectionMap: Map<string, boolean>) => void
  selectedServices: Map<string, boolean>
  isServicesOpen: boolean
  setServicesIsOpen: any
  isDomainsOpen: boolean
  setDomainsIsOpen: any
}

const ProjectFilters = ({
  domains,
  applyDomainFiltering,
  selectedDomains,
  services,
  applyServiceFiltering,
  selectedServices,
  isServicesOpen,
  setServicesIsOpen,
  isDomainsOpen,
  setDomainsIsOpen,
}: ProjectFiltersProps) => {
  const t = useTranslations('projects')

  const wrapperRef = useRef(null)
  useClickOutside(wrapperRef, () => {
    setServicesIsOpen(false)
    setDomainsIsOpen(false)
  })
  const sortedDomains = domains.sort(sorter('name'))
  const sortedServices = services.sort(sorter('name'))
  return (
    <div
      ref={wrapperRef}
      className="mb-2 flex select-none flex-wrap justify-center sm:max-h-4 sm:space-x-4 sm:overflow-visible"
    >
      <div className="z-10 mb-4 sm:mb-0">
        <FilterArea
          title={t('domains')}
          filters={sortedDomains}
          applyFiltering={applyDomainFiltering}
          selectedFilters={selectedDomains}
          isOpen={isDomainsOpen}
          setIsOpen={setDomainsIsOpen}
        />
      </div>

      <div className="z-10">
        <FilterArea
          title={t('services')}
          filters={sortedServices}
          applyFiltering={applyServiceFiltering}
          selectedFilters={selectedServices}
          isOpen={isServicesOpen}
          setIsOpen={setServicesIsOpen}
        />
      </div>
    </div>
  )
}

// COMPONENT

const ProjectsList = ({ data }: { data: ProjectsQuery }) => {
  const router = useRouter()
  const locale = getTypedLocale(router.locale)
  const t = useTranslations('projects')

  const services = data.allService.map((service) => ({
    id: service._id,
    name: (isLocalString(service.title) && service.title[locale]) || undefined,
    value: (isLocalString(service.title) && service.title[locale]) || undefined,
  }))

  const domains = data.allDomain.map((domain) => ({
    id: domain._id || undefined,
    name: (isLocalString(domain.title) && domain.title[locale]) || undefined,
    value: (isLocalString(domain.title) && domain.title[locale]) || undefined,
  }))

  const allProjects = data.allProject

  const [projects, setProjects] = useState(allProjects)
  useEffect(() => {
    // sort the projects randomly only on client side
    setProjects(allProjects?.sort(() => 0.5 - Math.random()))
  }, [allProjects])

  const [selectedServices, setSelectedServices] = useState<
    Map<string, boolean>
  >(new Map())
  const [selectedDomains, setSelectedDomains] = useState<Map<string, boolean>>(
    new Map(),
  )

  const generateInitialSelectionMap = (
    items: typeof domains | typeof services,
    preselectedItem?: string,
  ) => {
    const selectionMap = items
      .sort((a, b) => (a.name! > b.name! ? 1 : -1))
      .reduce(function (map, filter) {
        filter.id && map.set(filter.id, true)
        return map
      }, new Map<string, boolean>())

    if (preselectedItem && selectionMap.has(preselectedItem)) {
      selectionMap.forEach((value, key) => selectionMap.set(key, false))
      selectionMap.set(preselectedItem, true)
    }
    return selectionMap
  }

  useEffect(() => {
    if (!router.isReady) return
    const { serviceFilter, domainFilter } = router.query
    setSelectedServices(
      generateInitialSelectionMap(services, serviceFilter?.toString()),
    )
    setSelectedDomains(
      generateInitialSelectionMap(domains, domainFilter?.toString()),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  const filterProjectByDomain = (project: Project) => {
    for (const selectedDomain of getArrayFromMap(selectedDomains)) {
      if (
        project.projectDomains
          ?.map((domain) => domain?._id)
          .includes(selectedDomain)
      ) {
        return true
      }
    }
  }

  const filterProjectByService = (project: Project) => {
    for (const selectedService of getArrayFromMap(selectedServices)) {
      if (
        project.projectServices
          ?.map((service) => service?._id)
          .includes(selectedService)
      ) {
        return true
      }
    }
  }

  const getArrayFromMap = (selectionMap: Map<string, boolean>) => {
    const selectionMapCopy = new Map(selectionMap)
    selectionMapCopy.forEach((value, key) => {
      if (value === false) {
        selectionMapCopy.delete(key)
      }
    })
    return Array.from(selectionMapCopy.keys())
  }

  const [isServicesOpen, setServicesIsOpen] = useState(false)
  const [isDomainsOpen, setDomainsIsOpen] = useState(false)

  useEffect(() => {
    const filterProjects = () => {
      const filteredProjects = allProjects.filter(
        (project) =>
          filterProjectByDomain(project) && filterProjectByService(project),
      )
      setProjects(filteredProjects)
    }

    filterProjects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomains, selectedServices])

  return (
    <>
      <div className="py-3">
        <ProjectFilters
          domains={domains}
          applyDomainFiltering={setSelectedDomains}
          selectedDomains={selectedDomains}
          services={services}
          applyServiceFiltering={setSelectedServices}
          selectedServices={selectedServices}
          isServicesOpen={isServicesOpen}
          isDomainsOpen={isDomainsOpen}
          setServicesIsOpen={setServicesIsOpen}
          setDomainsIsOpen={setDomainsIsOpen}
        />
      </div>

      <Card
        noStyles
        noPadding
        className={cx(
          'flex flex-wrap',
          (isServicesOpen || isDomainsOpen) &&
            'sm:pointer-events-none sm:opacity-25',
        )}
      >
        {projects?.length > 0 ? (
          projects.map((project: any) => (
            <ProjectCard key={project.id || project._id} project={project} />
          ))
        ) : (
          <div className="mb-2/3 mx-5 w-full py-8 text-center text-lg">
            {t('empty')}
          </div>
        )}
      </Card>
    </>
  )
}

export default ProjectsList
